import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that manages the add-to-cart popup modal.
 * Shows a popup when items are added to cart with product details and recommendations.
 *
 * @typedef {object} CartItem
 * @property {string} image - The product image URL.
 * @property {string} product_title - The product title.
 * @property {number} price - The product price in cents.
 * @property {string} variant_title - The variant title.
 * @property {Record<string, string>} properties - The product properties.
 * @property {Array<{name: string, value: string}>} options_with_values - The variant options.
 *
 * @typedef {object} AddToCartPopupRefs
 * @property {DialogComponent} dialog - The dialog component.
 * @extends Component<AddToCartPopupRefs>
 */
export class AddToCartPopupComponent extends Component {
  requiredRefs = ['dialog'];
  
  /**
   * Gets the dialog component ref.
   * @returns {DialogComponent | undefined}
   */
  get dialog() {
    return this.refs.dialog;
  }

  /** @type {number | undefined} */
  #autoCloseTimeout;

  /** @type {number} */
  #autoCloseTime;

  connectedCallback() {
    super.connectedCallback();

    try {
      // Get auto-close time from data attribute (in seconds)
      const autoCloseTimeAttr = this.dataset.autoCloseTime;
      this.#autoCloseTime = autoCloseTimeAttr ? parseInt(autoCloseTimeAttr, 10) : 0;

      // Listen for cart add events
      document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

      // Handle view cart button click
      const viewCartButton = this.querySelector('.add-to-cart-notification__view-cart');
      if (viewCartButton) {
        viewCartButton.addEventListener('click', this.#handleViewCart);
      }
    } catch (error) {
      console.error('Error in add-to-cart-popup connectedCallback:', error);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    try {
      document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);

      if (this.#autoCloseTimeout) {
        clearTimeout(this.#autoCloseTimeout);
        this.#autoCloseTimeout = undefined;
      }
    } catch (error) {
      console.error('Error in add-to-cart-popup disconnectedCallback:', error);
    }
  }

  /**
   * Handles the cart add event to show the popup.
   * @param {CartAddEvent} event - The cart add event.
   */
  #handleCartAdd = (event) => {
    console.log('handleCartAdd', event);
    try {
      // Validate event structure
      if (!event || !event.detail) {
        return;
      }

      // Only show popup for successful additions
      if (event.detail.data?.didError) {
        return;
      }

      // Don't show popup for cart upsell additions
      const source = event.detail.data?.source || /** @type {any} */ (event.detail)?.source;
      if (source === 'cart-upsell') {
        return;
      }

      // Don't show popup for cart items component (deletions/quantity changes)
      if (source === 'cart-items-component') {
        return;
      }

      // Only show popup for actual product form additions
      if (source !== 'product-form-component') {
        return;
      }

      // Use addedProduct from event
      const addedProduct = /** @type {CartItem | undefined} */ (event.detail.data?.addedProduct);
      if (addedProduct && Object.keys(addedProduct).length > 0) {
        this.#showPopup(addedProduct);
      }
    } catch (error) {
      console.error('Error handling cart add event:', error);
    }
  };

  /**
   * Shows the popup with product details.
   * @param {CartItem} cartItem - The cart item to display.
   */
  #showPopup(cartItem) {
    try {
      // Get elements using querySelector
      const productImageContainer = /** @type {HTMLElement | null} */ (
        this.querySelector('.new-added-add-to-cart-popup__product-image')
      );
      const productName = /** @type {HTMLElement | null} */ (
        this.querySelector('.add-to-cart-popup__product-name .title-text')
      );
      const productPrice = /** @type {HTMLElement | null} */ (
        this.querySelector('.add-to-cart-popup__product-price')
      );
      const infoItems = /** @type {NodeListOf<HTMLElement>} */ (
        this.querySelectorAll('.add-to-cart-popup__info-item')
      );

      // Validate required elements
      if (!productName) {
        console.error('Product name element not found');
        return;
      }

      // Set product image
      if (productImageContainer && cartItem.image) {
        // Remove placeholder if exists
        const placeholder = productImageContainer.querySelector('.add-to-cart-popup__image-placeholder');
        if (placeholder) {
          placeholder.remove();
        }

        // Check if image already exists
        let productImage = productImageContainer.querySelector('img');
        if (!productImage) {
          productImage = document.createElement('img');
          productImage.className = 'add-to-cart-popup__product-image';
          productImage.width = 100;
          productImage.height = 100;
          productImage.loading = 'eager';
          productImage.fetchPriority = 'high';
          productImageContainer.appendChild(productImage);
        }

        productImage.src = cartItem.image;
        productImage.alt = cartItem.product_title || 'Product image';
      }

      // Set product name
      if (cartItem.product_title) {
        productName.textContent = cartItem.product_title.toUpperCase();
      }

      // Set product price
      if (productPrice && cartItem.price !== undefined) {
        // Try to update price - if it's a complex structure, we'll format it
        const formattedPrice = this.#formatPrice(cartItem.price);
        // Clear existing content and set new price
        productPrice.innerHTML = formattedPrice;
      }

      // Set product variant info
      if (infoItems.length > 0) {
        let optionIndex = 0;
        
        // First, display options_with_values
        if (cartItem.options_with_values && Array.isArray(cartItem.options_with_values)) {
          cartItem.options_with_values.forEach((option) => {
            const item = infoItems[optionIndex];
            if (optionIndex < infoItems.length && option && option.name && option.value && item) {
              item.textContent = `${option.name}: ${option.value}`;
              optionIndex++;
            }
          });
        }
        
        // Then, display properties (like "Game of the Week")
        if (cartItem.properties && optionIndex < infoItems.length) {
          console.log("cartItem.properties", cartItem.properties);
          const properties = cartItem.properties;
          const propertyKeys = Object.keys(properties);
          
          // Display all properties as key-value pairs
          propertyKeys.forEach(key => {
            if (optionIndex < infoItems.length && properties[key]) {
              const item = infoItems[optionIndex];
              if (item) {
                item.textContent = `${key}: ${properties[key]}`;
                optionIndex++;
              }
            }
          });
        }
        
        // Clear remaining info items
        for (let i = optionIndex; i < infoItems.length; i++) {
          const item = infoItems[i];
          if (item) {
            item.textContent = '';
          }
        }
      }

      // Show the dialog
      this.#showDialog();

      // Auto-close if configured
      if (this.#autoCloseTime > 0) {
        if (this.#autoCloseTimeout) {
          clearTimeout(this.#autoCloseTimeout);
        }
        this.#autoCloseTimeout = setTimeout(() => {
          this.close();
        }, this.#autoCloseTime * 1000);
      }
    } catch (error) {
      console.error('Error showing popup:', error);
    }
  }

  /**
   * Shows the popup dialog.
   */
  #showDialog() {
    try {
      // Validate dialog component exists
      if (!this.refs.dialog) {
        console.error('Dialog component not found');
        return;
      }

      // Safely show the dialog using the dialog-component
      const dialogEl = this.refs.dialog.querySelector('dialog');
      console.log('dialogEl', dialogEl);
      if (dialogEl && dialogEl.hasAttribute('open')) {
        dialogEl.scroll(0, 0);
      }
      this.refs.dialog.showDialog();

    } catch (error) {
      console.error('Error showing dialog:', error);
    }
  }

  /**
   * Handles view cart button click.
   */
  #handleViewCart = () => {
    try {
      const cartDrawer = /** @type {HTMLElement & { open?: () => void } | null} */ (
        document.querySelector('cart-drawer-component')
      );
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
      console.log('closing add-to-cart-popup',this);
      this.close();
    } catch (error) {
      console.error('Error opening cart drawer:', error);
    }
  };

  /**
   * Closes the popup dialog.
   */
  close() {
    try {
      if (this.#autoCloseTimeout) {
        clearTimeout(this.#autoCloseTimeout);
        this.#autoCloseTimeout = undefined;
      }

      if (this.refs.dialog) {
        this.refs.dialog.closeDialog();
      }
    } catch (error) {
      console.error('Error closing popup:', error);
    }
  }

  /**
   * Formats a price value to currency string.
   * @param {number} price - The price in cents.
   * @returns {string} The formatted price string.
   */
  #formatPrice(price) {
    try {
      // Validate price input
      if (typeof price !== 'number' || isNaN(price)) {
        console.warn('Invalid price value:', price);
        return '₹0.00';
      }

      // Use Shopify's money formatting if available, but force INR symbol
      if (window.Shopify && typeof /** @type {any} */ (window.Shopify).formatMoney === 'function') {
        // Format using Shopify, then replace currency symbol with ₹
        let formatted = /** @type {any} */ (window.Shopify).formatMoney(price);
        // Replace possible $ or other currency with ₹
        // Shopify by default does not localize currency code, more robust handling if theme uses currency switcher is possible
        formatted = formatted.replace(/[$€,£,USD,EUR,GBP]/g, '').replace(/^\s*/, '').replace(/^\s*/, ''); // removes currency symbols
        // Remove trailing/leading spaces and add ₹ in front
        return `₹${formatted.trim()}`;
      }

      // Fallback formatting with INR currency and Indian locale
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }).format(price / 100);
    } catch (error) {
      console.error('Error formatting price:', error);
      return '₹0.00';
    }
  }
}

if (!customElements.get('add-to-cart-popup-component')) {
  customElements.define('add-to-cart-popup-component', AddToCartPopupComponent);
}

